
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FeatureProps } from '../types';

const FeatureBlock: React.FC<FeatureProps> = ({ title, text, bullets }) => {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });

  return (
    <section className="py-24 md:py-32 overflow-hidden bg-black relative border-b border-white/5" ref={ref}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none overflow-hidden opacity-30">
        <div className="absolute top-0 left-0 w-[40%] h-[40%] bg-orange-600/5 blur-[120px] rounded-full"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          <motion.div 
            className="lg:col-span-7"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="mb-8 flex items-center gap-3">
              <div className="h-[1px] w-12 gradient-bg"></div>
              <span className="text-zinc-600 font-black text-[10px] uppercase tracking-[0.4em] font-space-grotesk">
                Infraestrutura DeFi
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[1.1] mb-8 uppercase italic">
              {title.split(' ').map((word: string, i: number) => (
                <span key={i} className={i % 2 !== 0 ? 'gradient-text block' : 'block'}>
                  {word}
                </span>
              ))}
            </h2>

            <div className="pl-0 lg:pl-8 border-l-0 lg:border-l border-white/10">
              <p className="text-lg md:text-xl lg:text-2xl text-zinc-400 leading-relaxed font-medium max-w-2xl">
                {text}
              </p>
            </div>
          </motion.div>

          <motion.div 
            className="lg:col-span-5 flex flex-col gap-4"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-1 gap-4">
              {bullets.map((bullet: string, idx: number) => (
                <div 
                  key={idx}
                  className="group relative p-6 bg-white/[0.02] backdrop-blur-md border border-white/5 rounded-2xl hover:border-orange-500/30 transition-all duration-500 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600/0 via-orange-600/[0.03] to-orange-600/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  
                  <div className="flex items-start gap-5 relative z-10">
                    <div className="mt-1 w-2 h-2 rounded-full gradient-bg shadow-[0_0_10px_rgba(249,115,22,0.5)] shrink-0"></div>
                    <div>
                      <span className="text-white font-bold text-sm md:text-base uppercase tracking-widest font-space-grotesk block mb-1">
                        {bullet}
                      </span>
                      <div className="h-[1px] w-0 group-hover:w-full bg-orange-600/30 transition-all duration-500"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default FeatureBlock;
